import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useUser } from '@/shared/hooks/useAuth';
import { Plus, TrendingUp, CornerDownRight, X } from 'lucide-react';
import toast from 'react-hot-toast';

interface IncentiveTarget {
  _id: string;
  employeeId: { _id: string; display_name: string; department?: string };
  period: string;
  targetAmount: number;
  revenueGenerated: number;
  achievedPercent: number;
  incentivePercent: number;
  incentiveAmount: number;
  assignedBy: { _id: string; display_name: string };
  source: string;
  status: string;
  carriedForwardAmount?: number;
  carriedFromPeriod?: string | null;
  // Present only when the immediately-prior period closed short and that
  // shortfall hasn't been accepted onto this row or declined yet.
  pendingCarryForward?: { sourcePeriod: string; shortfallAmount: number } | null;
}

function useTargets(period?: string) {
  return useQuery({
    queryKey: ['hr', 'incentive-targets', period],
    queryFn: async () => {
      const p = period ? `?period=${period}` : '';
      const { data } = await api.get(`/api/hr-incentives/targets${p}`);
      return data as { data: IncentiveTarget[]; summary: { totalTarget: number; totalRevenue: number; totalIncentive: number } };
    },
  });
}

function useMyTargets() {
  return useQuery({
    queryKey: ['hr', 'my-targets'],
    queryFn: async () => { const { data } = await api.get('/api/hr-incentives/targets/mine'); return data as { data: IncentiveTarget[] }; },
  });
}

function useEmployeeList() {
  return useQuery({
    queryKey: ['hr', 'employees-list'],
    // /api/v2/hr/employees never existed (404) — the staff list is GET /api/users.
    queryFn: async () => { const { data } = await api.get<{ users?: { id: string; display_name: string; department?: string }[] }>('/api/users'); return (data?.users ?? []).map((u) => ({ _id: u.id, display_name: u.display_name, department: u.department })); },
  });
}

function useAssignTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { employeeId: string; period: string; targetAmount: number; incentivePercent: number; source?: string }) => {
      const { data } = await api.post('/api/hr-incentives/targets', payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] }),
  });
}

function useUpdateRevenue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, revenueGenerated }: { id: string; revenueGenerated: number }) => {
      const { data } = await api.patch(`/api/hr-incentives/targets/${id}/revenue`, { revenueGenerated });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] }),
  });
}

function useCarryForward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, accept }: { id: string; accept: boolean }) => {
      const { data } = await api.post(`/api/hr-incentives/targets/${id}/carry-forward`, { accept });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] }),
  });
}

function useClearCarryForward() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/api/hr-incentives/targets/${id}/clear-carry-forward`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hr', 'incentive-targets'] }),
  });
}

function getCurrentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function IncentivesPage() {
  const user = useUser();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin' || (user?.department === 'hr' && ['manager', 'team_lead'].includes(user?.role ?? ''));

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Incentives & Targets</h1>
        <p className="text-sm text-muted-foreground mt-1">Assign revenue targets, track achievement, and calculate incentive payouts based on performance.</p>
      </div>
      {isAdmin ? <AdminIncentiveView /> : <EmployeeIncentiveView />}
    </div>
  );
}

export function AdminIncentiveView() {
  const [period, setPeriod] = useState(getCurrentPeriod());
  const { data, isLoading } = useTargets(period);
  const { data: employees } = useEmployeeList();
  const assignMut = useAssignTarget();
  const updateRevMut = useUpdateRevenue();
  const carryForwardMut = useCarryForward();
  const clearCarryForwardMut = useClearCarryForward();
  const [showNew, setShowNew] = useState(false);
  const [employeeId, setEmployeeId] = useState('');
  const [targetAmount, setTargetAmount] = useState('');
  const [incentivePercent, setIncentivePercent] = useState('');

  const handleAssign = async () => {
    if (!employeeId || !targetAmount || !incentivePercent) { toast.error('All fields are required.'); return; }
    try {
      await assignMut.mutateAsync({ employeeId, period, targetAmount: Number(targetAmount), incentivePercent: Number(incentivePercent), source: 'manual' });
      toast.success('Target assigned.');
      setShowNew(false); setEmployeeId(''); setTargetAmount(''); setIncentivePercent('');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Failed.'); }
  };

  if (isLoading) return <Skeleton className="h-[400px]" />;
  const targets = data?.data || [];
  const summary = data?.summary || { totalTarget: 0, totalRevenue: 0, totalIncentive: 0 };

  return (
    <>
      <Modal open={showNew} onClose={() => setShowNew(false)} title="Assign Target" footer={
        <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button><Button onClick={handleAssign} disabled={assignMut.isPending}>Assign</Button></div>
      }>
        <div className="space-y-3 py-3">
          <Select name="t-employee" label="Employee (BDE/Employee)" value={employeeId} onChange={e => setEmployeeId(e.target.value)} options={[{ value: '', label: 'Select...' }, ...(employees || []).map(e => ({ value: e._id, label: `${e.display_name} (${e.department || '-'})` }))]} />
          <Input name="t-target" label={`Target Amount for ${period} (₹)`} type="number" min="0" value={targetAmount} onChange={e => setTargetAmount(e.target.value)} placeholder="e.g. 500000" />
          <Input name="t-percent" label="Incentive %" type="number" min="0" max="100" value={incentivePercent} onChange={e => setIncentivePercent(e.target.value)} placeholder="e.g. 5" />
          <p className="text-xs text-muted-foreground">Incentive = Revenue Generated × Incentive%. Target determines goal tracking.</p>
        </div>
      </Modal>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border p-4 bg-info/10"><p className="text-xs text-muted-foreground">Total Target</p><p className="text-xl font-bold">₹{summary.totalTarget.toLocaleString('en-IN')}</p></div>
        <div className="rounded-lg border p-4 bg-success/10"><p className="text-xs text-muted-foreground">Revenue Generated</p><p className="text-xl font-bold text-success-text">₹{summary.totalRevenue.toLocaleString('en-IN')}</p></div>
        <div className="rounded-lg border p-4 bg-purple-50"><p className="text-xs text-muted-foreground">Incentive Payout</p><p className="text-xl font-bold text-purple-700">₹{summary.totalIncentive.toLocaleString('en-IN')}</p></div>
      </div>

      {/* Period selector + assign button */}
      <div className="flex items-center gap-3">
        <Input name="period" label="Period" type="month" value={period} onChange={e => setPeriod(e.target.value)} className="w-44" />
        <Button size="sm" onClick={() => setShowNew(true)} className="mt-5"><Plus className="h-4 w-4 mr-1" />Assign Target</Button>
      </div>

      {/* Targets Table */}
      <Card className="border-none shadow-sm">
        <CardHeader title="Employee Targets & Achievement" />
        <CardBody className="p-0">
          <div className="overflow-auto max-h-[450px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr><th className="px-4 py-3">Employee</th><th className="px-4 py-3 text-right">Target</th><th className="px-4 py-3 text-right">Revenue</th><th className="px-4 py-3 text-right">Achievement</th><th className="px-4 py-3 text-right">Incentive %</th><th className="px-4 py-3 text-right">Payout</th><th className="px-4 py-3">Actions</th></tr>
              </thead>
              <tbody className="divide-y">
                {targets.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No targets assigned for this period.</td></tr>}
                {targets.map(t => (
                  <tr key={t._id} className="hover:bg-muted/20 align-top">
                    <td className="px-4 py-3 font-medium">{t.employeeId?.display_name || '-'}</td>
                    <td className="px-4 py-3 text-right">
                      <div>₹{t.targetAmount?.toLocaleString('en-IN')}</div>
                      {!!t.carriedForwardAmount && (
                        <div className="mt-0.5 flex items-center justify-end gap-1 text-[11px] text-warning-text">
                          <CornerDownRight className="h-3 w-3" />
                          +₹{t.carriedForwardAmount.toLocaleString('en-IN')} carried from {t.carriedFromPeriod}
                          <button
                            type="button"
                            title="Clear carried-forward amount"
                            className="text-muted-foreground hover:text-danger-text"
                            onClick={() => clearCarryForwardMut.mutateAsync(t._id).then(() => toast.success('Carry-forward cleared.')).catch(() => toast.error('Failed.'))}
                          ><X className="h-3 w-3" /></button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-success-text">₹{t.revenueGenerated?.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-bold ${t.achievedPercent >= 100 ? 'text-success-text' : t.achievedPercent >= 70 ? 'text-warning-text' : 'text-danger-text'}`}>{t.achievedPercent?.toFixed(0)}%</span>
                    </td>
                    <td className="px-4 py-3 text-right">{t.incentivePercent}%</td>
                    <td className="px-4 py-3 text-right font-semibold text-purple-700">₹{t.incentiveAmount?.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => {
                          const rev = prompt(`Enter current revenue for ${t.employeeId?.display_name}:`, String(t.revenueGenerated || 0));
                          if (rev != null) updateRevMut.mutateAsync({ id: t._id, revenueGenerated: Number(rev) }).then(() => toast.success('Revenue updated.')).catch(() => toast.error('Failed.'));
                        }}><TrendingUp className="h-3.5 w-3.5" /></Button>
                        {t.pendingCarryForward && (
                          <div className="rounded-md border border-warning/25 bg-warning/10 px-2 py-1.5 text-[11px] leading-tight">
                            <p className="text-warning-text">
                              {t.employeeId?.display_name} missed {t.pendingCarryForward.sourcePeriod} by ₹{t.pendingCarryForward.shortfallAmount.toLocaleString('en-IN')}.
                            </p>
                            <div className="mt-1 flex gap-1.5">
                              <button
                                type="button"
                                className="rounded bg-warning px-2 py-0.5 font-medium text-white hover:bg-warning"
                                onClick={() => carryForwardMut.mutateAsync({ id: t._id, accept: true }).then(() => toast.success('Carried forward.')).catch((err) => toast.error(err?.response?.data?.error || 'Failed.'))}
                              >Carry forward</button>
                              <button
                                type="button"
                                className="rounded border border-warning/25 px-2 py-0.5 font-medium text-warning-text hover:bg-warning/10"
                                onClick={() => carryForwardMut.mutateAsync({ id: t._id, accept: false }).then(() => toast.success('Declined — shortfall absorbed.')).catch((err) => toast.error(err?.response?.data?.error || 'Failed.'))}
                              >Decline</button>
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </>
  );
}

export function EmployeeIncentiveView() {
  const { data, isLoading } = useMyTargets();
  if (isLoading) return <Skeleton className="h-[300px]" />;
  const targets = data?.data || [];

  return (
    <Card className="border-none shadow-sm">
      <CardHeader title="My Targets & Incentives" />
      <CardBody className="p-0">
        <div className="overflow-auto max-h-[500px]">
          {targets.length === 0 ? (
            <p className="px-4 py-8 text-center text-muted-foreground">No targets assigned to you yet.</p>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 sticky top-0">
                <tr><th className="px-4 py-3">Period</th><th className="px-4 py-3 text-right">Target</th><th className="px-4 py-3 text-right">Revenue</th><th className="px-4 py-3 text-right">Achievement</th><th className="px-4 py-3 text-right">Incentive Earned</th></tr>
              </thead>
              <tbody className="divide-y">
                {targets.map(t => (
                  <tr key={t._id} className="hover:bg-muted/20">
                    <td className="px-4 py-3 font-medium">{t.period}</td>
                    <td className="px-4 py-3 text-right">
                      <div>₹{t.targetAmount?.toLocaleString('en-IN')}</div>
                      {!!t.carriedForwardAmount && (
                        <div className="mt-0.5 text-[11px] text-warning-text">+₹{t.carriedForwardAmount.toLocaleString('en-IN')} carried from {t.carriedFromPeriod}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-success-text">₹{t.revenueGenerated?.toLocaleString('en-IN')}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-20 h-2 rounded-full bg-gray-200 overflow-hidden"><div className={`h-full rounded-full ${t.achievedPercent >= 100 ? 'bg-success' : t.achievedPercent >= 70 ? 'bg-warning' : 'bg-danger'}`} style={{ width: `${Math.min(100, t.achievedPercent)}%` }} /></div>
                        <span className="text-xs font-medium">{t.achievedPercent?.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-purple-700">₹{t.incentiveAmount?.toLocaleString('en-IN')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
